import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "common/components/Button/Button";
import useAuth from "common/hooks/useAuth";
import { TrainingExercise } from "types/TrainingExercise";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { Specy } from "types/Specy";
import { BsFeather } from "react-icons/bs";

function TrainingExerciseItem({ item, onDelete, onNavigate }: any) {
  const { id, name, isArchived, species } = item as TrainingExercise;

  const { user: currentUser } = useAuth();
  const { t } = useTranslation();

  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["training-grid"]?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["training-grid"]?.update)) ??
    false;
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["training-grid"]?.delete)) ??
    false;

  const detailLink = `/training-exercises/edit/${id}`;

  const containerColor = isArchived ? "opacity-65" : "";

  return (
    <div
      className={`responsive-flex-lg px-4 py-4 mt-2 w-full relativ cursor-pointer rounded-lg border border-slate-150 transition-all bg-white ${containerColor}`}
    >
      <Link
        to={detailLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none responsive-flex-lg flex-1"
            : "responsive-flex-lg flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-start lg:px-6">
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="flex flex-row justify-between">
                <div>
                  <h4 className="text-xl font-bold text-primary leading-6 mt-1">
                    {name}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <TooltipWrapper
          className="mr-1"
          content={
            species?.length > 0 ? (
              <div className="flex flex-col flex-wrap gap-1">
                {species?.map((specy: Specy) =>
                  specy ? (
                    <span className="font-bold">
                      {specy.name && specy.scientificName
                        ? `${specy.name} (${specy.scientificName})`
                        : specy.name ?? specy.scientificName}
                    </span>
                  ) : null
                )}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <BsFeather className="w-3 h-3" />
            <span className="font-bold">
              {t("forms.speciesLength", {
                count: species?.length || 0,
              })}
            </span>
          </div>
        </TooltipWrapper>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <TooltipWrapper
            content={
              <span>
                {isArchived ? t("common.restore") : t("common.archived")}
              </span>
            }
          >
            <Button
              type="error-line"
              icon={isArchived ? "RiInboxUnarchiveLine" : "RiInboxArchiveLine"}
              compact
              onClick={() => onDelete(item.id)}
              disabled={!canDeleteItem}
              confirmMessage={
                isArchived
                  ? t("actions.confirmRestore")
                  : t("actions.confirmArchive")
              }
            />
          </TooltipWrapper>

          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canEditItem}
          />
        </div>
      </div>
    </div>
  );
}

export default TrainingExerciseItem;
